import React from 'react';
import { 
  DataGrid, 
  GridToolbar,
  GridActionsCellItem
} from '@mui/x-data-grid';
import { Box, Paper, Chip } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// Компонент таблицы журнала аудита
const AuditLogTable = ({ data, loading, pagination }) => {
  const navigate = useNavigate();
  
  // Форматирование даты
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('ru-RU', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  // Функция для сокращения значения
  const truncateValue = (value) => {
    if (!value) return '-';
    if (typeof value === 'string' && value.length > 50) {
      return value.substring(0, 50) + '...';
    }
    return value;
  };

  // Обработчик для просмотра деталей записи
  const handleView = (id) => {
    navigate(`/audit_logs/${id}`);
  };

  // Определение колонок таблицы
  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { 
      field: 'username', 
      headerName: 'Пользователь', 
      width: 150,
      valueGetter: (params) => params.row.username || `ID: ${params.row.user_id}`
    },
    { 
      field: 'action', 
      headerName: 'Действие', 
      width: 120,
      renderCell: (params) => {
        let color;
        if (params.value.includes('create')) {
          color = 'success';
        } else if (params.value.includes('update')) {
          color = 'primary';
        } else if (params.value.includes('delete')) {
          color = 'error';
        } else if (params.value.includes('approve')) {
          color = 'success';
        } else if (params.value.includes('reject')) {
          color = 'error';
        } else {
          color = 'default';
        }
        
        // Определение читаемой метки
        const actionMap = {
          'user.create': 'Создание пользователя',
          'user.update': 'Обновление пользователя',
          'user.delete': 'Удаление пользователя',
          'kyc.approve': 'Одобрение KYC',
          'kyc.reject': 'Отклонение KYC',
          'transaction.create': 'Создание транзакции',
          'transaction.update': 'Обновление транзакции',
          'transaction.delete': 'Удаление транзакции',
          'create': 'Создание',
          'update': 'Обновление',
          'delete': 'Удаление'
        };
        
        const label = actionMap[params.value] || params.value;
        
        return <Chip label={label} color={color} variant="outlined" />;
      }
    },
    { field: 'entity_type', headerName: 'Тип сущности', width: 130 },
    { field: 'entity_id', headerName: 'ID сущности', width: 110 },
    { 
      field: 'old_value', 
      headerName: 'Старое значение', 
      width: 200,
      valueGetter: (params) => truncateValue(params.value)
    },
    { 
      field: 'new_value', 
      headerName: 'Новое значение', 
      width: 200,
      valueGetter: (params) => truncateValue(params.value)
    },
    { 
      field: 'created_at', 
      headerName: 'Дата', 
      width: 170,
      valueGetter: (params) => formatDate(params.value)
    },
    {
      field: 'actions',
      headerName: 'Действия',
      width: 100,
      type: 'actions',
      getActions: (params) => [
        <GridActionsCellItem
          icon={<VisibilityIcon />}
          label="Просмотр"
          onClick={() => handleView(params.id)}
        />
      ]
    }
  ];

  // Настройка пагинации
  const paginationModel = pagination ? {
    page: pagination.page,
    pageSize: pagination.rowsPerPage
  } : { page: 0, pageSize: 10 };

  // Обработчик изменения пагинации
  const handlePaginationModelChange = (newModel) => {
    if (pagination && pagination.onPageChange) {
      pagination.onPageChange(newModel.page);
    }
    
    if (pagination && pagination.onRowsPerPageChange && newModel.pageSize !== paginationModel.pageSize) {
      pagination.onRowsPerPageChange(newModel.pageSize);
    }
  };

  return (
    <Paper elevation={2} sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={data}
          columns={columns}
          loading={loading}
          
          // Серверная пагинация
          paginationModel={paginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          onPaginationModelChange={handlePaginationModelChange}
          paginationMode="server"
          rowCount={pagination?.count || 0}
          
          disableRowSelectionOnClick
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            '& .MuiDataGrid-cell:hover': {
              color: 'primary.main',
            },
          }}
        />
      </Box>
    </Paper>
  );
};

// Проверка типов пропсов
AuditLogTable.propTypes = {
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  pagination: PropTypes.shape({
    page: PropTypes.number.isRequired,
    rowsPerPage: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
    onPageChange: PropTypes.func,
    onRowsPerPageChange: PropTypes.func
  })
};

// Значения по умолчанию
AuditLogTable.defaultProps = {
  loading: false
};

export default AuditLogTable; 